import { withTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { Settings } from '@mui/icons-material'
import {
  Box,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { spacing, useMediaQuery } from '@mui/system'
import { ApexOptions } from 'apexcharts'
import { round } from 'lodash'
import Chart from 'react-apexcharts'

import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { ThemeProps } from '@/types/theme'

import { DaiAlert } from './DaiAlerts'

const Card = styled(MuiCard)(spacing)
const Spacer = styled.div(spacing)
const ChartWrapper = styled.div`
  max-height: 350px;
  width: 100%;
`

type ChartDataItem = {
  id: string | null
  value: number | null
}

type Props = ThemeProps & {
  loading?: boolean
  data?: ChartDataItem[]
  title: string
  onSettingsPress?: () => void
  onLabelClick?: (id: string) => void
  BodyComponent?: React.ReactNode
  noDataMessage?: string
}

const PieChart = ({
  theme,
  loading = false,
  data = [],
  title,
  onSettingsPress,
  onLabelClick,
  BodyComponent,
  noDataMessage,
}: Props) => {
  const { t } = useT()
  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.success.light,
    theme.palette.warning.light,
    theme.palette.error.light,
    theme.palette.info.light,
  ]
  const hideLegend = useMediaQuery(theme.breakpoints.down('md'))

  // Sort data descending by value
  const sortedData = [...data]
    .filter(
      (item): item is ChartDataItem & { value: number } => item.value !== null,
    )
    .sort((a, b) => b.value - a.value)

  const chartData = sortedData.map((item, index) => ({
    ...item,
    color: colorPalette[index % colorPalette.length],
  }))

  const options: ApexOptions = {
    labels: chartData.map(d => `${d.id}: ${formatCurrency(round(d.value))}`),
    colors: chartData.map(d => d.color),
    tooltip: {
      enabled: true,
      followCursor: true,
      y: {
        formatter: () => ``,
        title: {
          formatter: (seriesName: string) => seriesName,
        },
      },
    },
    legend: {
      show: !hideLegend,
      labels: {
        colors: theme.palette.text.primary,
      },
      offsetX: 100,
    },
    chart: {
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const categoryId = chartData[config.dataPointIndex]?.id
          onLabelClick?.(categoryId || '')
        },
      },
    },
  }

  return (
    <Card mb={1}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={'space-between'}
        >
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {onSettingsPress && (
            <IconButton
              onClick={onSettingsPress}
              sx={{ alignSelf: 'flex-end', mb: 1 }}
              size="small"
            >
              <Settings />
            </IconButton>
          )}
        </Stack>

        <Spacer mb={6} />

        <ChartWrapper>
          {BodyComponent ||
            (loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            ) : chartData.length === 0 ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <DaiAlert
                  message={noDataMessage || t('deliciousTable.noData')}
                />
              </Box>
            ) : (
              <Chart
                options={options}
                series={chartData.map(d => d.value ?? 0)}
                type="pie"
                height="350"
              />
            ))}
        </ChartWrapper>
      </CardContent>
    </Card>
  )
}

export default withTheme(PieChart)
